import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPatch, apiPost } from "@/shared/api/client";
import { invalidateTenantScopes } from "@/shared/api/invalidate";
import { queryKeys } from "@/shared/api/query-keys";
import type {
  BillingSummaryDto,
  ChangeWhen,
  ItemsChangeResponse,
  OrderDto,
  PeriodChangeResponse,
  PlanItemSelection,
  ReissueOrderResultDto,
  RenewNowBody,
  ScheduledChangeUndoDto,
  SubscriptionStateDto,
  TenantItemsDto,
} from "@/shared/api/types";
import { useTenantId } from "@/shared/auth/session";
import { usePermissions } from "./usePermissions";

/**
 * Every write that changes what an organisation has bought (ADR-0015 §3.6). They live in `shared/` because
 * three modules start them: Settings → Subscription, the locked API-keys and Webhooks panels, and the
 * limit notices in the pair-device and add-person dialogs.
 *
 * None of them moves the plan by itself. `PATCH /billing/items` with more of something answers an order to
 * pay and nothing changes until the gateway's webhook is applied; with less of something it schedules the
 * reduction for the renewal. So each one invalidates and the caller then watches — never an optimistic write.
 * A trial is the one exception, and not an optimistic one either: its answer is the new item rows themselves
 * (`useStartItemTrial`).
 */

/**
 * `billing:manage` — Owner-only by preset and never held by an API key (ADR-0006). Every surface that
 * offers to buy something asks this first, so nobody is shown a button the api would refuse.
 */
export function useBillingActionsPermission(): boolean {
  return usePermissions().can("billing", "manage");
}

/** A change to quantities touches entitlements, the organisation's own status and every gate that reads them. */
function useBillingInvalidation() {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return async () => {
    await Promise.all([
      invalidateTenantScopes(queryClient, tenantId, ["billing", "settings", "organisation"]),
      queryClient.invalidateQueries({ queryKey: queryKeys.session.me() }),
    ]);
  };
}

/**
 * More → a prorated order to pay today (`when: "now"`, the api's default) or a quantity written against
 * the renewal invoice (`when: "renewal"`, nothing to pay); fewer → scheduled for the renewal either way
 * (`PLAN_DOWNGRADE_BLOCKED` over usage). The choice is the customer's, made in the Review dialog (§14).
 */
export function useChangeItems() {
  const invalidate = useBillingInvalidation();
  return useMutation({
    mutationFn: ({ items, when }: { items: readonly PlanItemSelection[]; when?: ChangeWhen }) =>
      apiPatch<ItemsChangeResponse>("/billing/items", when === undefined ? { items } : { items, when }),
    onSuccess: invalidate,
  });
}

/**
 * Monthly ↔ yearly. `renewal` schedules it and charges nothing; `now` is monthly → yearly ONLY (the api
 * answers `PERIOD_CHANGE_AT_RENEWAL_ONLY` otherwise) and raises a yearly order for every held line whose
 * period, once paid, runs a year from today plus the unused days of the current month (ADR-0015 addendum).
 */
export function useChangePeriod() {
  const invalidate = useBillingInvalidation();
  return useMutation({
    mutationFn: ({ period, when }: { period: "monthly" | "yearly"; when?: ChangeWhen }) =>
      apiPatch<PeriodChangeResponse>("/billing/period", when === undefined ? { period } : { period, when }),
    onSuccess: invalidate,
  });
}

/**
 * End the subscription at the period end (§14): no renewal invoice is raised, the organisation is
 * suspended `subscription_ended` on the date, data is kept and the terminals keep working. A trial is
 * refused with `SUBSCRIPTION_IN_TRIAL` — it simply ends.
 */
export function useCancelSubscription() {
  const invalidate = useBillingInvalidation();
  return useMutation({
    mutationFn: (reason?: string) =>
      apiPost<SubscriptionStateDto>("/billing/cancel", reason ? { reason } : {}),
    onSuccess: invalidate,
  });
}

/** Withdraw that cancellation while the period is still running. */
export function useResumeSubscription() {
  const invalidate = useBillingInvalidation();
  return useMutation({
    mutationFn: () => apiPost<SubscriptionStateDto>("/billing/resume", {}),
    onSuccess: invalidate,
  });
}

/**
 * Undo one entry of `GET /billing`'s `scheduledChanges`, by replaying the call the api itself attached to
 * it. The portal does not work out how to reverse a scheduled change — a quantity undo is a different
 * request from a period undo, and a cancellation undo is a third — it sends back what the api said to
 * send. That keeps one authority over the semantics and makes a new scheduled kind a server-only change.
 */
export function useUndoScheduledChange() {
  const invalidate = useBillingInvalidation();
  return useMutation({
    mutationFn: (undo: ScheduledChangeUndoDto) =>
      undo.method === "PATCH"
        ? apiPatch<unknown>(undo.path, undo.body ?? {})
        : apiPost<unknown>(undo.path, undo.body ?? {}),
    onSuccess: invalidate,
  });
}

/**
 * A purchase that starts a fresh period on payment: what "Subscribe again" is after a cancelled
 * subscription has ended, and what the configurator's `subscribe` mode leads to (ADR-0015 §3, D9).
 */
export function useCreateOrder() {
  const invalidate = useBillingInvalidation();
  return useMutation({
    mutationFn: ({ period, items }: { period: "monthly" | "yearly"; items: readonly PlanItemSelection[] }) =>
      apiPost<OrderDto>("/billing/orders", { period, items }),
    onSuccess: invalidate,
  });
}

/**
 * Renewing early (M11 follow-up §4): the next period's invoice, raised today at the scheduled quantities
 * and — when nothing else is pending — at whichever period the customer chooses in the dialog.
 *
 * Paying it does NOT start the next period early. The worker rolls the window from the current end and
 * keeps the anniversary day, so the plan simply continues when today's period runs out; that is the whole
 * point of the button, and the card says so in those words once `paidThrough` comes back set.
 *
 * Refused while an order is already open (`ORDER_OPEN`, with the open order's id), in a trial
 * (`SUBSCRIPTION_IN_TRIAL`), while cancelling (`SUBSCRIPTION_CANCELLING`) and on anything not running
 * (`SUBSCRIPTION_NOT_ACTIVE`).
 */
export function useRenewNow() {
  const invalidate = useBillingInvalidation();
  return useMutation({
    mutationFn: (body: RenewNowBody) => apiPost<OrderDto>("/billing/renew-now", body),
    onSuccess: invalidate,
  });
}

/**
 * An add-on's own trial, once per item ever (`TRIAL_ALREADY_USED`); no card, nothing to pay. Started ONLY
 * from `StartTrialDialog`, which every "Try free" opens first (ADR-0017 addendum of 2026-10-01, second).
 *
 * **The one write here that is not watched for.** The trial is the api's own switch, so its answer IS the
 * organisation's item rows after it — not a guess at them — and it goes straight into `GET /billing`'s
 * cache: the comparison row reads "In trial until …" on the same frame, where invalidating alone left it
 * on "Not held" until the refetch landed (Kumar, 2026-10-01). Everything else that depends on the items
 * held is then invalidated: the entitlements (the switcher draws the module's workspace from them), `/me`,
 * the estimates, and the summary itself for the api's re-priced comparison. None of that is awaited — a
 * slow refetch must never keep the dialog spinning over a trial that has already started.
 *
 * Its opposites are gone with the item list: removing something is `PATCH /billing/items` with a quantity
 * of 0 and taking that back is the api's own `undo` call, replayed by `useUndoScheduledChange`. Two doors
 * to one room with different results is exactly what the M11 follow-up found (finding 1).
 */
export function useStartItemTrial() {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: ({ code, quantity }: { code: string; quantity?: number }) =>
      apiPost<TenantItemsDto>(`/billing/items/${code}/trial`, quantity === undefined ? {} : { quantity }),
    onSuccess: (items) => {
      queryClient.setQueryData<BillingSummaryDto>(queryKeys.tenant.billing.summary(tenantId), (summary) =>
        summary ? { ...summary, items } : summary,
      );
      void Promise.all([
        invalidateTenantScopes(queryClient, tenantId, ["billing", "settings", "organisation"]),
        queryClient.invalidateQueries({ queryKey: queryKeys.catalogue.estimates(tenantId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.session.me() }),
      ]);
    },
  });
}

/**
 * A fresh invoice for the same lines. This is what "Pay now" calls on an expired renewal — including from
 * the suspended interstitial, where it is the only way back (runbooks/billing.md).
 */
export function useReissueOrder() {
  const invalidate = useBillingInvalidation();
  return useMutation({
    mutationFn: (orderId: string) => apiPost<ReissueOrderResultDto>(`/billing/orders/${orderId}/reissue`, {}),
    onSuccess: invalidate,
  });
}
